/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, List, Modal, Text } from '@/shared/ui';

/** What went wrong, already in the buyer's words. */
export interface CheckoutIssues {
  /** The dialog's title: what kind of problem this is. */
  title: string;
  /** One sentence per problem, each naming the field it is about. */
  items: string[];
  /** A second chance the buyer can take from the dialog, when there is one. */
  retry?: { label: string; onClick: () => void };
}

/**
 * The checkout's one place for bad news.
 *
 * Errors used to land in the summary, under the total, in the smallest type
 * on the page, and at high zoom they pushed the summary past the fold: the
 * buyer clicked pay and saw nothing happen. A dialog cannot be missed and
 * does not move the layout it sits over.
 *
 * @param props.issues - What to say, or null when there is nothing to.
 * @param props.onClose - Dismisses the dialog.
 * @returns The dialog.
 */
export function CheckoutIssuesDialog({ issues, onClose }: {
  issues: CheckoutIssues | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('@pages/onboarding');
  const items = issues?.items ?? [];

  return (
    <Modal opened={issues !== null} onClose={onClose} title={issues?.title} size="sm">
      <div
        data-testid="checkout-issues-dialog"
        className="flex flex-col gap-(--space-4)"
        style={{ padding: 'var(--space-2) 0' }}
      >
        <List spacing="xs" className="m-0 ps-(--space-5) list-disc">
          {items.map((item) => (
            <List.Item key={item} className="list-item">
              <Text style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)' }}>
                {item}
              </Text>
            </List.Item>
          ))}
        </List>
        <div className="flex flex-wrap justify-end gap-(--space-2)">
          {issues?.retry && (
            <Button
              type="button"
              variant="subtle"
              onClick={() => { issues.retry?.onClick(); onClose(); }}
            >
              {issues.retry.label}
            </Button>
          )}
          <Button
            type="button"
            data-testid="checkout-issues-close"
            onClick={onClose}
            className="bg-(--seed-base) hover:bg-(--seed-base)/90 text-(--text-inverse)"
          >
            {t('checkoutStep.issues.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
